import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, LayoutGrid, Plus, Search } from "lucide-react";
import {
  getLandlordWorkspaces,
  getSingleUser,
} from "../context/functions/AdminFunction";
import Loading from "../components/loader/Loading";
import CreateWorkspaceModal from "../components/landlord/CreateWorkspaceModal";
import WorkspaceManager, {
  imageUrl,
} from "../components/landlord/WorkspaceManager";
import "../styles/landlord-workspaces.css";

const searchText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export default function LandlordWorkspaces() {
  const { landlordid } = useParams();
  const [state, setState] = useState({
    id: null,
    workspaces: [],
    landlord: null,
    error: "",
  });
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [creating, setCreating] = useState(false);
  const previousWorkspaceIds = useRef(null);
  const loading = state.id !== landlordid;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    Promise.all([
      getLandlordWorkspaces(landlordid, controller.signal),
      getSingleUser(landlordid, "landlord"),
    ])
      .then(([response, landlord]) => {
        if (!active || response?.status === "canceled") return;
        const workspaces = response?.success_res?.workspaces;
        if (!Array.isArray(workspaces))
          throw new Error("Invalid workspace response");
        setState({ id: landlordid, workspaces, landlord, error: "" });
        if (previousWorkspaceIds.current) {
          const created = workspaces.find(
            (workspace) => !previousWorkspaceIds.current.includes(workspace.id),
          );
          if (created) setSelectedId(created.id);
          previousWorkspaceIds.current = null;
        }
      })
      .catch((error) => {
        if (active)
          setState({
            id: landlordid,
            workspaces: [],
            landlord: null,
            error:
              error?.response?.status === 404
                ? "Landlord not found."
                : "Unable to load workspaces. Please try again.",
          });
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [landlordid, attempt]);

  const workspaces = loading ? [] : state.workspaces;
  const selected =
    workspaces.find((workspace) => workspace.id === selectedId) ||
    workspaces[0];
  const filtered = workspaces.filter(
    (workspace) =>
      searchText(workspace.name || "Unnamed workspace").includes(
        searchText(query).trim(),
      ) || searchText(workspace.id).includes(searchText(query).trim()),
  );
  const landlordName = [
    state.landlord?.fname_companyName,
    state.landlord?.lname,
  ]
    .filter(Boolean)
    .join(" ");
  const reload = () => {
    setState((current) => ({ ...current, id: null }));
    setAttempt((value) => value + 1);
  };

  return (
    <section
      className="landlord-workspaces lw-management"
      aria-label="Landlord workspace management"
    >
      {loading ? (
        <div className="lw-state">
          <Link to="/landlord" className="lw-back">
            <ArrowLeft size={16} /> Back to landlords
          </Link>
          <div className="lw-loading" role="status" aria-label="Loading workspaces">
            <Loading />
          </div>
        </div>
      ) : state.error ? (
        <div className="lw-state">
          <Link to="/landlord" className="lw-back">
            <ArrowLeft size={16} /> Back to landlords
          </Link>
          <div className="lw-empty" role="alert">
            <p>{state.error}</p>
            <button type="button" className="lw-button" onClick={reload}>
              Try again
            </button>
          </div>
        </div>
      ) : (
        <div className="lw-management-layout">
          <aside
            className="lw-workspace-sidebar"
            aria-label="Landlord workspaces"
          >
            <Link to="/landlord" className="lw-back">
              <ArrowLeft size={16} /> Back to landlords
            </Link>
            <div className="lw-landlord-label">
              <strong>{landlordName || "Landlord"}</strong>
              <small>{state.landlord?.email}</small>
            </div>
            <p className="lw-eyebrow">
              WORKSPACES <span>{workspaces.length}</span>
            </p>
            {workspaces.length > 5 && (
              <label className="lw-sidebar-search">
                <Search size={15} aria-hidden="true" />
                <input
                  aria-label="Search workspaces"
                  placeholder="Search workspaces"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
            )}
            <div className="lw-workspace-options">
              {filtered.map((workspace) => (
                <button
                  type="button"
                  key={workspace.id}
                  aria-pressed={selected?.id === workspace.id}
                  onClick={() => setSelectedId(workspace.id)}
                  className={`lw-workspace-nav ${selected?.id === workspace.id ? "is-active" : ""}`}
                >
                  <span className="lw-workspace-icon" aria-hidden="true">
                    {workspace.workspaceImage ? (
                      <img src={imageUrl(workspace.workspaceImage)} alt="" />
                    ) : (
                      (workspace.name || "W").slice(0, 1).toUpperCase()
                    )}
                  </span>
                  <span>{workspace.name || "Unnamed workspace"}</span>
                </button>
              ))}
            </div>
            {query && filtered.length === 0 && (
              <p className="lw-panel-note">No matching workspaces.</p>
            )}
            <button
              type="button"
              className="lw-add-workspace"
              onClick={() => setCreating(true)}
            >
              <span className="lw-workspace-icon">
                <Plus size={18} />
              </span>{" "}
              Add new workspace
            </button>
          </aside>
          <div className="lw-management-main">
            {selected ? (
              <WorkspaceManager
                key={`${landlordid}/${selected.id}`}
                landlordId={landlordid}
                workspaceId={selected.id}
                workspaceName={selected.name}
                onRenamed={(name) =>
                  setState((current) => ({
                    ...current,
                    workspaces: current.workspaces.map((workspace) =>
                      workspace.id === selected.id
                        ? { ...workspace, name }
                        : workspace,
                    ),
                  }))
                }
              />
            ) : (
              <div className="lw-management-content">
                <header className="lw-header">
                  <h1>Workspaces</h1>
                </header>
                <div className="lw-empty">
                  <LayoutGrid size={28} />
                  <h3>No workspaces yet</h3>
                  <p>Add a workspace for this landlord to get started.</p>
                  <button
                    className="lw-button lw-primary"
                    type="button"
                    onClick={() => setCreating(true)}
                  >
                    Add workspace
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {creating && (
        <CreateWorkspaceModal
          landlordId={landlordid}
          onClose={() => setCreating(false)}
          onCreated={() => {
            previousWorkspaceIds.current = workspaces.map(
              (workspace) => workspace.id,
            );
            setCreating(false);
            setQuery("");
            reload();
          }}
        />
      )}
    </section>
  );
}
